import { computed, ref } from 'vue';

/**
 * 客户操作耗时学习器，版本 0.1.69，更新时间：2026-09-05 16:30 CST。
 *
 * 首次使用业务实测基准值；后续只收集成功完成的端到端耗时，并保留最近 30 次样本。
 * 数据仅包含毫秒数并保存在当前浏览器，不包含账号、域名、IP 或配置内容。
 */
export function createOperationTiming(storageKey: string, fallbackMs: number) {
  const samples = ref<number[]>(readSamples(storageKey));
  const startedAt = ref<number>();
  const lastResult = ref<{ elapsedMs: number; ok: boolean }>();
  let clearResultTimer: ReturnType<typeof setTimeout> | undefined;

  const averageMs = computed(() => {
    if (!samples.value.length) return fallbackMs;
    return samples.value.reduce((total, value) => total + value, 0) / samples.value.length;
  });

  const hint = computed(() => {
    if (startedAt.value !== undefined) return `预计约 ${formatDuration(averageMs.value)}生效`;
    if (lastResult.value) {
      const prefix = lastResult.value.ok ? '本次生效' : '本次失败';
      return `${prefix} ${formatDuration(lastResult.value.elapsedMs)} · 平均 ${formatDuration(averageMs.value)}`;
    }
    return '';
  });

  function start() {
    // 连续操作会重置起点，以“最后一次操作后多久真正生效”为统计口径。
    if (clearResultTimer) clearTimeout(clearResultTimer);
    startedAt.value = Date.now();
    lastResult.value = undefined;
  }

  function succeed() {
    if (startedAt.value === undefined) return;
    const elapsedMs = Math.max(1, Date.now() - startedAt.value);
    samples.value = [...samples.value, elapsedMs].slice(-30);
    writeSamples(storageKey, samples.value);
    lastResult.value = { elapsedMs, ok: true };
    startedAt.value = undefined;
    clearResultLater();
  }

  function fail() {
    if (startedAt.value === undefined) return;
    lastResult.value = { elapsedMs: Math.max(1, Date.now() - startedAt.value), ok: false };
    startedAt.value = undefined;
    clearResultLater();
  }

  function clearResultLater() {
    if (clearResultTimer) clearTimeout(clearResultTimer);
    // 操作结果仅临时展示 15 秒，历史样本继续留在浏览器用于校准后续估时。
    clearResultTimer = setTimeout(() => {
      lastResult.value = undefined;
      clearResultTimer = undefined;
    }, 15_000);
  }

  function dispose() {
    if (clearResultTimer) clearTimeout(clearResultTimer);
  }

  return { hint, averageMs, samples, start, succeed, fail, dispose };
}

function formatDuration(milliseconds: number): string {
  const seconds = milliseconds / 1000;
  return seconds < 10 ? `${seconds.toFixed(1)} 秒` : `${Math.round(seconds)} 秒`;
}

function readSamples(storageKey: string): number[] {
  if (typeof window === 'undefined') return [];
  try {
    const value = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
    return Array.isArray(value)
      ? value.filter(item => typeof item === 'number' && Number.isFinite(item) && item > 0).slice(-30)
      : [];
  } catch {
    return [];
  }
}

function writeSamples(storageKey: string, samples: number[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(samples));
  } catch {
    // 浏览器禁用本地存储时仍保留当前页面内统计，不阻断策略发布。
  }
}
